import * as React from 'react';

import isNil from 'lodash/isNil';

import { AutocompleteInput, IAutocompleteOption } from '@opendoor/bricks';
import { searchListings } from '../../api';

export interface IListingTypeAheadProps {
  // Required:
  name: string;
  onSelect: (name: string, address: IListingTypeAheadOption) => void;
  // Optional:
  ctaProps?: any;
  invalidUserInputMessage?: string;
  placeholderText?: string;
  showCta?: boolean;
}

export interface IListingTypeAheadOption {
  agentEmail: string;
  readableAddress: string;
  slug: string;
  state: string;
}

export interface IListingTypeAheadAutocompleteOption extends IAutocompleteOption {
  metadata: IListingTypeAheadMetadata;
}

interface IListingTypeAheadMetadata {
  agentEmail: string;
  state?: string;
}

interface IListingTypeAheadState {
  addressOptions: IListingTypeAheadAutocompleteOption[];
}

export default class ListingTypeAheadInput extends React.PureComponent<
  IListingTypeAheadProps,
  IListingTypeAheadState
> {
  public state = {
    addressOptions: [] as IListingTypeAheadAutocompleteOption[],
  };

  // Refrence to current query value in order to check in the
  // future whether our returned query value matches the current
  // query value
  private currentQuery = '';

  public getAddresses = (query: string) => {
    if (query.length < 3) {
      this.setState({ addressOptions: [] as IListingTypeAheadAutocompleteOption[] });
      return;
    }

    this.currentQuery = query;

    searchListings(query, { offerable: true, limit: 5 }).then(listings => {
      if (query === this.currentQuery) {
        const addressOptions = listings.map(listing => {
          return {
            displayValue: listing.readable_address,
            metadata: { state: listing.state, agentEmail: listing.agent_email },
            value: listing.slug,
          } as IListingTypeAheadAutocompleteOption;
        });
        this.setState({ addressOptions });
      }
    });
  };

  public selectAddress = (address: IListingTypeAheadAutocompleteOption) => {
    const { name, onSelect } = this.props;
    // Testing for the slug, we would expect it to be undefined if no address
    if (!address.value) {
      return;
    }

    onSelect(name, {
      agentEmail: address.metadata.agentEmail,
      readableAddress: address.displayValue,
      slug: address.value,
      state: address.metadata.state,
    });
  };

  public render(): JSX.Element {
    const { ctaProps, invalidUserInputMessage, placeholderText, showCta } = this.props;
    const { addressOptions } = this.state;

    return (
      <AutocompleteInput
        ctaProps={ctaProps}
        invalidUserInputMessage={
          isNil(invalidUserInputMessage) ? 'Please enter a valid address' : invalidUserInputMessage
        }
        options={addressOptions}
        placeholderText={isNil(placeholderText) ? 'Enter the property address...' : placeholderText}
        select={this.selectAddress}
        showCta={showCta}
        updateOptions={this.getAddresses}
        validationThreshold={4}
      />
    );
  }
}
